import { REAL_TO_EDITOR_RATE, INIT_Y, BINDING_DEFAULT_Y, DEFAULT_FLOWER_BINDING_Y } from '../../variable/constant'
import createStem from '../../line/createStem'
import getPointsArr from '../../line/getPointsArr'
import { reomveObjectFromScene } from '../../utils/index'

class SpriteFlowerObject {
    constructor(initData) {
        let { resource, position, env, params, id } = initData;
        this.id = id || 'flower_' + new Date().getTime();
        this.resource = resource;
        this.env = env;
        this.renderType = 'sprite';
        this.isGlbResource = false;
        this.modelMesh = {
            flowerMesh: null,
            stemMesh: null,
            touchHelperMesh: null
        };
        this.realMesh = {
            flowerMesh: null,
            stemMesh: null,
        };
        this.params = {
            bindingY: DEFAULT_FLOWER_BINDING_Y,
            faceIndex: 1,
            size: 1,
            visible: true,
            showStem: true,
            locked: false,
            ...(params || {})
        }
        if (this.params.bindingY === undefined || this.params.bindingY === null) {
            this.params.bindingY = DEFAULT_FLOWER_BINDING_Y;
        }
        if (this.params.visible === undefined) {
            this.params.visible = true;
        }
        if (this.params.showStem === undefined) {
            this.params.showStem = true;
        }
        if (this.params.stemRadius === undefined) {
            this.params.stemRadius = this.resource.detail.stem.radiusStem - 0;
        }
        if (this.params.locked === undefined) {
            this.params.locked = false;
        }
        this.init(position)
        this.initPromise = Promise.resolve(this);
    }

    getStartPosition() {
        if (this.env.getBindingStartPosition) {
            return this.env.getBindingStartPosition(this);
        }
        return this.env.objects.jsContainer.position;
    }

    isBoundToStudioContainer() {
        return !!(
            this.env.isStudioMode &&
            this.env.isStudioMode() &&
            this.params.containerId
        );
    }

    getStemBaseY(startPosition = this.getStartPosition()) {
        return this.isBoundToStudioContainer() ? (parseFloat(startPosition.y) || 0) : 0;
    }

    getBindingY(startPosition = this.getStartPosition()) {
        return this.getStemBaseY(startPosition) + BINDING_DEFAULT_Y + this.params.bindingY;
    }

    getInitialY(startPosition = this.getStartPosition()) {
        if (!this.isBoundToStudioContainer()) {
            return INIT_Y;
        }
        const stemBaseY = this.getStemBaseY(startPosition);
        const studioMaxInitialY = 360;
        const studioTargetOffset = 320;
        const studioMinOffset = 120;
        return Math.max(
            stemBaseY + studioMinOffset,
            Math.min(stemBaseY + studioTargetOffset, studioMaxInitialY)
        );
    }

    init(position) {
        const detail = this.resource.detail;
        const flowerMesh = new THREE.Mesh(
            new THREE.CylinderGeometry(
                (detail.flower.radiusTop - 0) * REAL_TO_EDITOR_RATE,
                (detail.flower.radiusBottom - 0) * REAL_TO_EDITOR_RATE,
                (detail.flower.height - 0) * REAL_TO_EDITOR_RATE,
                32
            ),
            new THREE.MeshPhysicalMaterial({ color: detail.flower.color })
        );
        let startPosition = this.getStartPosition();

        if (position) {
            flowerMesh.position.set(position.x, position.y, position.z);
        } else {
            let shifting = sessionStorage.getItem('shifting') || 0;
            let viewVec3 = new THREE.Vector3().copy(this.env.three.viewController.object.position);
            viewVec3 = viewVec3.divideScalar(viewVec3.length());
            const initialY = this.getInitialY(startPosition);
            let initVec3 = new THREE.Vector3(startPosition.x, initialY, startPosition.z).addScaledVector(viewVec3, shifting - 0);
            if (this.isBoundToStudioContainer()) {
                initVec3.y = Math.min(initVec3.y, initialY);
            }
            flowerMesh.position.set(initVec3.x, initVec3.y, initVec3.z);
            sessionStorage.setItem('shifting', shifting - 0 + 5);
        }

        this.modelMesh.flowerMesh = flowerMesh;
        flowerMesh.parentObject = this;
        this.env.three.scene.add(flowerMesh);

        let stemMesh = createStem({
            startPosition,
            position: flowerMesh.position,
            bindingType: this.env.params.render.bindingType,
            bindingY: this.getBindingY(startPosition),
            bottomY: this.getStemBaseY(startPosition),
            dom: this.env.params.dom,
            materialOption: {
                color: detail.stem.color,
                linewidth: this.params.stemRadius
            }
        })
        this.modelMesh.stemMesh = stemMesh;
        this.env.three.scene.add(stemMesh);

        const touchHelperMesh = new THREE.Mesh(
            new THREE.SphereGeometry(10, 32, 16),
            new THREE.MeshBasicMaterial({ color: detail.flower.color })
        );
        this.modelMesh.touchHelperMesh = touchHelperMesh;
        touchHelperMesh.visible = false;
        touchHelperMesh.parentObject = this;
        this.env.three.scene.add(touchHelperMesh);

        if (position) {
            this.updateModel();
        }
    }

    updateModel(sourceStartPosition) {
        let startPosition = this.getStartPosition();
        const stemBaseY = this.getStemBaseY(startPosition);

        this.modelMesh.flowerMesh.scale.x = this.params.size;
        this.modelMesh.flowerMesh.scale.y = this.params.size;
        this.modelMesh.flowerMesh.scale.z = this.params.size;
        const bindingY = this.getBindingY(startPosition);

        if (sourceStartPosition) {
            let sourcePosition = this.modelMesh.flowerMesh.position;
            sourcePosition.x = (sourcePosition.x - sourceStartPosition.x) + startPosition.x;
            sourcePosition.y = (sourcePosition.y - sourceStartPosition.y) + startPosition.y;
            sourcePosition.z = (sourcePosition.z - sourceStartPosition.z) + startPosition.z;
        }

        this.modelMesh.flowerMesh.lookAt(startPosition.x, bindingY, startPosition.z);
        this.modelMesh.flowerMesh.rotateX(-Math.PI / 2.0);

        this.modelMesh.stemMesh.geometry.setPositions(
            getPointsArr({
                startPosition,
                position: this.modelMesh.flowerMesh.position,
                bindingType: this.env.params.render.bindingType,
                bindingY: bindingY,
                bottomY: stemBaseY
            })
        )
        this.modelMesh.stemMesh.material.linewidth = this.params.stemRadius;
    }

    clearReal(modelVisiable = true) {
        if (this.realMesh.flowerMesh) {
            reomveObjectFromScene(this.env.three.scene, this.realMesh.flowerMesh);
            this.realMesh.flowerMesh = null;
        }
        if (this.realMesh.stemMesh) {
            reomveObjectFromScene(this.env.three.scene, this.realMesh.stemMesh);
            this.realMesh.stemMesh = null;
        }
        if (this.modelMesh.touchHelperMesh) {
            this.modelMesh.touchHelperMesh.visible = false;
        }
        const visible = this.params.visible && modelVisiable;
        this.modelMesh.flowerMesh.visible = visible;
        this.modelMesh.stemMesh.visible = visible && this.params.showStem;
    }

    setVisible(visible) {
        this.params.visible = visible;
        if (!visible) {
            this.clearReal(false);
            if (this.modelMesh.touchHelperMesh) {
                this.modelMesh.touchHelperMesh.visible = false;
            }
            this.modelMesh.flowerMesh.visible = false;
            this.modelMesh.stemMesh.visible = false;
            return;
        }
        this.modelMesh.flowerMesh.visible = true;
        this.modelMesh.stemMesh.visible = this.params.showStem;
    }

    setStemVisible(showStem) {
        this.params.showStem = showStem;
        this.modelMesh.stemMesh.visible = this.params.visible && showStem;
        if (!showStem && this.realMesh.stemMesh) {
            reomveObjectFromScene(this.env.three.scene, this.realMesh.stemMesh);
            this.realMesh.stemMesh = null;
        }
    }

    setStemRadius(stemRadius) {
        this.params.stemRadius = Math.max(1, Math.round(stemRadius - 0 || 1));
        this.modelMesh.stemMesh.material.linewidth = this.params.stemRadius;
        if (this.realMesh.stemMesh) {
            this.realMesh.stemMesh.material.linewidth = this.params.stemRadius;
        }
    }

    setLocked(locked) {
        this.params.locked = locked;
    }

    getLayerData() {
        return {
            id: this.id,
            name: this.resource.name,
            resourceId: this.resource.id,
            visible: this.params.visible,
            showStem: this.params.showStem,
            stemRadius: this.params.stemRadius,
            locked: this.params.locked
        };
    }

    remove() {
        this.env.three.objController.detach();
        if (this.realMesh.flowerMesh) {
            reomveObjectFromScene(this.env.three.scene, this.realMesh.flowerMesh);
            reomveObjectFromScene(this.env.three.scene, this.realMesh.stemMesh);
        }
        reomveObjectFromScene(this.env.three.scene, this.modelMesh.touchHelperMesh);
        reomveObjectFromScene(this.env.three.scene, this.modelMesh.flowerMesh);
        reomveObjectFromScene(this.env.three.scene, this.modelMesh.stemMesh);
        for (let i = 0; i < this.env.objects.flowers.length; i++) {
            if (this.id === this.env.objects.flowers[i].id) {
                this.env.objects.flowers.splice(i, 1);
                break;
            }
        }
        if (this.env.selectedFlowerId === this.id) {
            this.env.clearFlowerSelection();
        }
        this.env.syncLayerState();
    }

    changeResource(resource) {
        this.resource = resource;
        this.modelMesh.flowerMesh.material.color.set(this.resource.detail.flower.color);
        this.modelMesh.stemMesh.material.color.set(this.resource.detail.stem.color);
    }

    rotate(val) {
        let faceIndex = this.params.faceIndex + val;
        const angles = this.resource.detail.angles[0];
        const maxLength = angles.length;
        if (faceIndex > maxLength) {
            faceIndex = 1;
        }
        if (faceIndex < 1) {
            faceIndex = maxLength;
        }
        this.params.faceIndex = faceIndex;
        return faceIndex;
    }
}

export default SpriteFlowerObject;
